import {companyService} from "@/src/modules/company/services";
import {moment} from "@/src/modules/core/lib";
import {
  dispatchCenterRequiresShift,
  isDispatchOperatorRole,
} from "@/src/modules/core/lib/dispatch-center-policy";
import {useMeHasPermission, useMeStore, useStoredMe} from "@/src/modules/core/store/me";
import {ParamsOptions, type Me} from "@/src/modules/core/types";
import {Role} from "@/src/modules/users/types";

import {shiftsApi} from "../api";
import {getShiftPropertyPlacements} from "../lib/get-shift-property-placements";
import {getShiftTypeItemsAvailableForRole} from "../lib/emergency-shift-eligibility";
import {useShiftsStore} from "../store";
import {
  AcknowledgeShiftRequest,
  AcknowledgeShiftResponse,
  AddPatrolVisitRequest,
  AdminActionRequest,
  BulkCopyRequest,
  BulkScheduleRequest,
  CheckPatrolVisitRequest,
  CopyRequest,
  EmergencyShiftSchema,
  ScheduleRequest,
  Shift,
  ShiftCoverageRow,
  ShiftRequest,
  ShiftType,
  SuggestShiftBreaksData,
  SuggestShiftBreaksParams,
  TemplateRequest,
} from "../types";
import {normalizeShiftBreaks} from "../lib/shift-breaks";
import {shiftsSerializer} from "./serializer";

class ShiftsService {
  get shiftTypes() {
    return [
      {label: "Normal", value: ShiftType.NORMAL},
      {label: "Patrol", value: ShiftType.PATROL},
      {label: "Dispatch", value: ShiftType.DISPATCH},
    ];
  }

  async getShifts(options: ParamsOptions) {
    const {data} = await shiftsApi.getShifts(options);
    const shifts = data.data;
    const meta = data.meta;

    useShiftsStore.setState({
      shifts,
    });

    return {shifts, meta};
  }

  async getShiftCoverage(options: ParamsOptions = {}) {
    const {data} = await shiftsApi.getShiftCoverage(options);
    const shiftCoverage = data.data.map((row) => this.normalizeShiftCoverageRow(row));
    const meta = data.meta;

    return {
      success: data.success,
      message: data.message,
      shiftCoverage,
      meta,
    };
  }

  async getActiveShift() {
    const options = {
      filter: {
        status: ["active"],
      },
      page: {
        offset: 0,
        limit: 1,
      },
    };
    const {data} = await shiftsApi.getShifts(options);
    const shifts = data.data;

    const shift = shifts[0] ? normalizeShiftBreaks(shifts[0]) : null;

    useShiftsStore.setState({activeShift: shift});

    return {shift};
  }

  async getShiftById(shiftId: number, options?: ParamsOptions) {
    const {
      data: {data},
    } = await shiftsApi.getShift(shiftId, options);
    return data;
  }

  async suggestShiftBreaks(params: SuggestShiftBreaksParams): Promise<SuggestShiftBreaksData> {
    const {data} = await shiftsApi.suggestShiftBreaks(params);
    return data.data;
  }

  async createShift(data: ShiftRequest) {
    const {
      data: {data: shift},
    } = await shiftsApi.createShift(data);

    useShiftsStore.setState((state) => {
      const exists = state.shifts.some((s) => s.shift_id === shift.shift_id);
      return {
        shifts: exists
          ? state.shifts.map((s) => (s.shift_id === shift.shift_id ? shift : s))
          : [...state.shifts, shift],
      };
    });

    return shift;
  }

  async checkPatrolVisit(shiftId: number, visitId: number, data: CheckPatrolVisitRequest) {
    const {
      data: {data: visit},
    } = await shiftsApi.checkPatrolVisit(shiftId, visitId, data);
    return visit;
  }

  async addPatrolVisit(shiftId: number, data: AddPatrolVisitRequest) {
    const {
      data: {data: visit},
    } = await shiftsApi.addPatrolVisit(shiftId, data);
    return visit;
  }

  async deletePatrolVisit(shiftId: number, visitId: number) {
    await shiftsApi.deletePatrolVisit(shiftId, visitId);
  }

  async updateShift(shiftId: number, data: ShiftRequest) {
    const shifts = useShiftsStore.getState().shifts;

    // optimistic update
    const optimisticShifts = shifts.map((shift) => {
      if (shift.shift_id === shiftId) {
        const {user_id, from_datetime, to_datetime} = data;
        return {...shift, from_datetime, to_datetime, user: {id: user_id}} as Shift;
      }

      return shift;
    });

    useShiftsStore.setState({
      shifts: optimisticShifts,
    });

    try {
      const {
        data: {data: updatedShift},
      } = await shiftsApi.updateShift(shiftId, data);
      const newShifts = shifts.map((shift) => {
        if (shift.shift_id === shiftId) {
          return updatedShift;
        }
        return shift;
      });

      useShiftsStore.setState({
        shifts: newShifts,
      });
    } catch (e) {
      useShiftsStore.setState({
        shifts,
      });
      throw e;
    }
  }

  async deleteShift(shiftId: number, notifyOfficer: boolean, forceDelete?: boolean) {
    const shifts = useShiftsStore.getState().shifts;
    const newShifts = shifts.filter((shift) => shift.shift_id !== shiftId);
    useShiftsStore.setState({
      shifts: newShifts,
    });

    try {
      await shiftsApi.deleteShift(shiftId, notifyOfficer, forceDelete);
    } catch (e) {
      useShiftsStore.setState({
        shifts,
      });
      throw e;
    }
  }

  async getTemplates(options: ParamsOptions) {
    const {data} = await shiftsApi.getTemplates(options);
    const templates = data.data;
    const meta = data.meta;

    return {templates, meta};
  }

  async getTemplateById(templateId: number) {
    const {
      data: {data},
    } = await shiftsApi.getTemplateById(templateId);
    return data;
  }

  async createTemplate(data: TemplateRequest) {
    const {data: template} = await shiftsApi.createTemplate(data);
    return template;
  }

  async updateTemplate(templateId: number, data: TemplateRequest) {
    const {data: template} = await shiftsApi.updateTemplate(templateId, data);
    return template;
  }

  async deleteTemplate(templateId: number) {
    await shiftsApi.deleteTemplate(templateId);
  }

  async copy(data: CopyRequest) {
    const {data: response} = await shiftsApi.copy(data);
    return response;
  }

  async bulkCopy(bulkData: BulkCopyRequest) {
    const {
      data: {data},
    } = await shiftsApi.bulkCopy(bulkData);

    return data;
  }

  async schedule(data: ScheduleRequest) {
    const {data: response} = await shiftsApi.schedule(data);
    return response;
  }

  async bulkSchedule(bulkData: BulkScheduleRequest) {
    const {
      data: {data},
    } = await shiftsApi.bulkSchedule(bulkData);

    return data;
  }

  async bulkDelete(bulkData: BulkCopyRequest) {
    const {data} = await shiftsApi.bulkDelete(bulkData);

    return data;
  }

  async adminAction(actionData: AdminActionRequest) {
    const {data} = await shiftsApi.adminAction(actionData);

    return data;
  }

  async createEmergencyShift(formData: EmergencyShiftSchema) {
    const me = useMeStore.getState().me;
    if (!me) throw new Error("User not found");

    const serializedData = shiftsSerializer.serializeEmergencyShift(formData);
    const {data} = await shiftsApi.createEmergencyShift(serializedData);

    return data;
  }

  recalculateShiftDates(shift: Shift, newDate: string) {
    const fromTime = moment(shift.from_datetime).format("HH:mm:ss");
    const fromDatetime = moment(shift.from_datetime).utc();
    const newFromDatetime = moment(`${newDate}T${fromTime}`).utc();

    const diff = newFromDatetime.diff(fromDatetime, "milliseconds");

    const newToDatetime = moment(shift.to_datetime).utc().add(diff, "milliseconds");

    const breaks = shift.breaks.map((b) => {
      const newBreakDatetime = moment(b.start_datetime).utc().add(diff, "milliseconds");

      return {
        ...b,
        start_datetime: newBreakDatetime.format(),
      };
    });

    return {
      from_datetime: newFromDatetime.format(),
      to_datetime: newToDatetime.format(),
      breaks,
    };
  }

  getIsMultiplePropertiesForType(type: "normal" | "patrol" | "dispatch") {
    return ["patrol", "dispatch"].includes(type);
  }

  getPropertyFilterForShiftType(type: "normal" | "patrol" | "dispatch" | undefined) {
    if (type === "dispatch") {
      return {status: 1, is_dispatch: 1 as const};
    }

    return {status: 1, is_hidden: 0 as const};
  }

  getCanCreateShift() {
    const canCreate = useMeHasPermission("shift").store;
    return canCreate;
  }

  getCanUpdateShift() {
    const canUpdate = useMeHasPermission("shift").update;
    return canUpdate;
  }

  getCanDeleteShift() {
    const canDelete = useMeHasPermission("shift").delete;
    return canDelete;
  }

  getCanAdjustShift() {
    const canAdjust = useMeHasPermission("shift").adjust;
    return canAdjust;
  }

  getIsShiftAdjustable(shift: Shift | null) {
    const canUpdate = this.getCanUpdateShift();

    if (!shift) return false;

    const toDateTime = moment(shift.to_datetime);

    return canUpdate && (shift.status === "completed" || moment().isSameOrAfter(toDateTime));
  }

  getIsShiftSchedulable(shift: Shift | null) {
    const canUpdate = this.getCanUpdateShift();

    if (!shift) return false;

    return canUpdate && shift.status === "unscheduled" && !!shift.user?.id;
  }

  getIsShiftEditable(shift: Shift | null) {
    const canUpdate = this.getCanUpdateShift();

    if (!shift) return false;
    const isActionable = this.getIsShiftActionable(shift);

    return canUpdate && isActionable;
  }

  getIsShiftDeleteable(shift: Shift) {
    const canDelete = this.getCanDeleteShift();
    const canAdminDeletePast = this.getCanAdminDeletePastScheduled(shift);
    if (!canDelete) return false;
    if (this.getIsShiftActionable(shift)) return true;
    return canAdminDeletePast;
  }

  /** Unscheduled, or scheduled with start at or after now — calendar drag + edit/publish affordances. */
  getIsShiftCalendarActionable(shift: Shift) {
    return this.getIsShiftActionable(shift);
  }

  /** Past scheduled Admin/SS delete — API expects `force_delete: true`. */
  getShouldForceDeleteShift(shift: Shift | null | undefined) {
    const canDelete = this.getCanDeleteShift();
    const adminPast = this.getCanAdminDeletePastScheduled(shift);
    return canDelete && adminPast;
  }

  private getCanAdminDeletePastScheduled(shift: Shift | null | undefined) {
    const me = useStoredMe();
    if (!shift) return false;
    if (!me?.isAdmin && !me?.isSeniorSupervisor) return false;
    if (shift.status !== "scheduled") return false;
    return moment(shift.from_datetime).isBefore(moment(), "minute");
  }

  private getIsShiftActionable(shift: Shift) {
    return shift.status === "unscheduled" || shift.status === "scheduled";
  }

  getRolesEnabledForShifts(): Role["name"][] {
    return [
      "officer",
      "patrol officer",
      "supervisor",
      "senior supervisor",
      "post commander",
      "dispatch",
      "dispatch supervisor",
    ];
  }

  /** Managed `/shifts` (Schedule / Manage / Coverage) — officers use `/home` only. */
  getCanAccessShiftsManagement(me: Me | null | undefined) {
    if (!me) return false;
    return !me.isOfficer;
  }

  /** Managed Shifts Schedule tab — dispatch must not see the staff calendar. */
  getCanViewShiftScheduleCalendar(me: Me | null | undefined) {
    if (!this.getCanAccessShiftsManagement(me) || !me) return false;
    return !me.isDispatch;
  }

  /** Managed Shifts Coverage tab — dispatch operators use dispatch desk coverage instead. */
  getCanViewShiftCoverage(me: Me | null | undefined) {
    if (!this.getCanAccessShiftsManagement(me)) return false;
    return !isDispatchOperatorRole(me);
  }

  /** Roles that see OfficerHome on `/home` — keep in sync with post-login redirect. */
  isShiftsHomeRole(me: Me | null | undefined) {
    if (!me) return false;
    return (
      me.isOfficer ||
      me.isPatrolOfficer ||
      me.isSupervisor ||
      me.isSeniorSupervisor ||
      me.isDispatch ||
      me.isPostCommander ||
      me.isDispatchSupervisor
    );
  }

  /** Personal shift queries (`active-shift`, upcoming `shifts` list) — not shift-coverage. */
  getIsShiftsEnabledForRole(me?: Me | null) {
    const resolved = me === undefined ? useStoredMe() : me;
    if (!resolved || resolved.isResident) return false;
    if (!this.isShiftsHomeRole(resolved)) return false;
    if (isDispatchOperatorRole(resolved) && !dispatchCenterRequiresShift(resolved)) return false;
    return true;
  }

  getRoleHasMultipleProperties(role: Role) {
    return role.multiple_properties === 1;
  }

  getIsRoleFlexibleBreak() {
    const me = useStoredMe()!;
    const flexibleRoles = companyService.getBreaksFlexibleRoles() as unknown as number[];

    return flexibleRoles.includes(me.role.id);
  }

  getRoleMustFilterByUserProperties() {
    const me = useStoredMe()!;
    const roleName = me.role.name.trim().toLowerCase();
    return ["post commander", "dispatch supervisor"].includes(roleName);
  }

  clear() {
    useShiftsStore.setState({
      activeShift: null,
      shifts: [],
      draggedShift: null,
    });
  }

  clearActiveShift() {
    useShiftsStore.setState({
      activeShift: null,
    });
  }

  async acknowledgeShift(data: AcknowledgeShiftRequest): Promise<AcknowledgeShiftResponse> {
    const {data: response} = await shiftsApi.acknowledgeShift(data);
    return response;
  }

  getRolesAvailableForType(type: "normal" | "patrol" | "dispatch") {
    const roles = this.getRolesEnabledForShifts();
    const dispatchRoles = ["dispatch", "dispatch supervisor"] as const;
    const patrolRoles = ["officer", "patrol officer", "supervisor", "senior supervisor"] as const;
    const nonDispatchRoles = roles.filter(
      (role) => !dispatchRoles.includes(role as (typeof dispatchRoles)[number]),
    );

    if (type === "patrol") {
      return roles.filter((role) =>
        patrolRoles.includes(role as (typeof patrolRoles)[number]),
      );
    }

    if (type === "dispatch") {
      return roles.filter((role) => dispatchRoles.includes(role as (typeof dispatchRoles)[number]));
    }

    return nonDispatchRoles;
  }

  /** Dispatch supervisor may only schedule dispatch-type shifts for dispatch roles. */
  getIsDispatchShiftManager(me: Me | null | undefined) {
    return Boolean(me?.isDispatchSupervisor);
  }

  getRolesAvailableForShiftManagement(me?: Me | null) {
    const user = me ?? useMeStore.getState().me;
    if (this.getIsDispatchShiftManager(user)) {
      return this.getRolesAvailableForType(ShiftType.DISPATCH);
    }
    return this.getRolesEnabledForShifts();
  }

  getShiftTypesAvailableForMe() {
    const me = useMeStore.getState().me;
    if (!me) return [];
    // Admin / senior supervisor schedule any shift type; role.name is not in getRolesEnabledForShifts().
    if (me.isAdmin || me.isSeniorSupervisor || me.isSuperAdmin) {
      return this.shiftTypes;
    }
    if (me.isDispatchSupervisor) {
      return this.shiftTypes.filter((item) => item.value === ShiftType.DISPATCH);
    }
    if (me.isPostCommander) {
      return this.shiftTypes.filter((item) => item.value === ShiftType.NORMAL);
    }
    return getShiftTypeItemsAvailableForRole(me.role.name, this.shiftTypes, (t) =>
      this.getRolesAvailableForType(t),
    );
  }

  getRoleHasBreaks(role: number | null) {
    const rolesWithoutBreaks = companyService.getRolesWithoutBreaks();
    return role ? !rolesWithoutBreaks.includes(role) : true;
  }

  getRoleHasPropertySelectorInEmergencyShift() {
    const me = useMeStore.getState().me;
    if (!me) return false;
    return !["dispatch", "dispatch supervisor"].includes(me.role.name);
  }

  getDatesData(shifts: Shift[], dates: (string | null)[], isWeekly: boolean) {
    const data = new Map<string, Shift[]>();

    if (!shifts.length) {
      return data;
    }

    shifts.sort((a, b) => {
      return moment(`${a.from_datetime}`).diff(moment(`${b.from_datetime}`));
    });

    dates
      .filter((date) => date !== null)
      .forEach((date) => {
        shifts
          .filter((shift) => {
            return moment(shift.from_datetime).isSame(date, "day");
          })
          .forEach((shift) => {
            const key = isWeekly ? `${shift.user?.id ?? "unassigned"}-${date}` : date;

            if (!data.has(key)) {
              data.set(key, []);
            }

            data.get(key)!.push(shift);
          });
      });

    return data;
  }

  getPropertyDatesData(shifts: Shift[], dates: (string | null)[]) {
    const calendarShifts = new Map<string, Shift[]>();

    const validDates = dates.filter((date): date is string => date !== null);

    const sortedShifts = [...shifts].sort((a, b) =>
      moment(`${a.from_datetime}`).diff(moment(`${b.from_datetime}`)),
    );

    for (const shift of sortedShifts) {
      const placements = getShiftPropertyPlacements(shift);

      for (const date of validDates) {
        if (!moment(shift.from_datetime).isSame(date, "day")) {
          continue;
        }

        for (const placement of placements) {
          const key = `${placement.id}-${date}`;
          const existing = calendarShifts.get(key) ?? [];
          if (existing.some((s) => s.shift_id === shift.shift_id)) {
            continue;
          }
          calendarShifts.set(key, [...existing, shift]);
        }
      }
    }

    for (const [, cellShifts] of calendarShifts) {
      cellShifts.sort((a, b) =>
        moment(`${a.from_datetime}`).diff(moment(`${b.from_datetime}`)),
      );
    }

    return calendarShifts;
  }

  private normalizeShiftCoverageRow(
    row: ShiftCoverageRow & {user?: {telephone?: string | null} | null},
  ): ShiftCoverageRow {
    const officerTelephone = row.telephone?.trim() || row.user?.telephone?.trim() || null;

    return {
      ...row,
      officer_name: row.officer_name,
      telephone: officerTelephone,
      assigned: {
        properties: (row.assigned?.properties ?? []).map((property) => ({
          ...property,
          site_telephone: property.site_telephone?.trim() || null,
        })),
      },
      last_known: row.last_known
        ? {
            latitude: row.last_known.latitude,
            longitude: row.last_known.longitude,
            recorded_at: row.last_known.recorded_at,
          }
        : null,
    };
  }
}

export const shiftsService = new ShiftsService();
